import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";

import { useAuth } from "../../../contexts/AuthContext";
import { usePermissions } from "../../../hooks/usePermissions";
import { billingApi, PricingResponse } from "../../../lib/api/billing";
import { creditsApi, CreditDetailsResponse } from "../../../lib/api/credits";
import { analytics } from "@lib/analytics";
import { SubscriptionDto, PlanType, PricingPlan } from "@optidev/shared-types";
import { PricingCard } from "./PricingCard";
import { CurrentPlanCardWithCredits } from "./CurrentPlanCardWithCredits";
import { TopUpCreditsModal } from "./TopUpCreditsModal";
import { EmbedBillingView } from "./EmbedBillingView";
import { useEmbedStore } from "../../../stores/embed-store";
import { Switch } from "../../../components/ui/switch";
import { Label } from "../../../components/ui/label";
import { Alert, AlertDescription } from "../../../components/ui/alert";
import { Loader2, AlertCircle, Globe, Sparkles, CreditCard } from "lucide-react";
import toast from "react-hot-toast";
import { Button } from "../../../components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../components/ui/select";

// Static plan info (features, descriptions)
const PLAN_INFO: Record<string, { name: string; description: string; features: string[]; featureHeader?: string; ctaText?: string; monthlyCredits?: number }> = {
  [PlanType.STANDARD]: {
    name: "Standard",
    description: "For individuals and small teams",
    monthlyCredits: 100,
    featureHeader: "Everything in Free, plus:",
    features: [
      "Haiku & Sonnet AI models",
      "Public, private & internal apps",
      "Custom domain support",
      "Usage-based OptiDev Cloud hosting",
    ],
  },
  [PlanType.PRO]: {
    name: "Pro",
    description: "For growing teams",
    monthlyCredits: 200,
    featureHeader: "Everything in Standard, plus:",
    features: [
      "Haiku, Sonnet & Opus AI models",
      "SSO/SAML authentication",
    ],
  },
  [PlanType.ENTERPRISE]: {
    name: "Enterprise",
    description: "For large organizations",
    featureHeader: "Everything in Pro, plus:",
    features: [
      "Dedicated support team",
      "Onboarding services",
      "Forward deploy engineering",
      "Custom integrations & connections",
    ],
    ctaText: "Book a demo",
  },
};

// Currency symbols for formatting
const CURRENCY_SYMBOLS: Record<string, string> = {
  usd: "$",
  gbp: "£",
  eur: "€",
  cad: "C$",
  aud: "A$",
  jpy: "¥",
};

// Currency display names
const CURRENCY_LABELS: Record<string, string> = {
  usd: "USD ($)",
  gbp: "GBP (£)",
  eur: "EUR (€)",
  cad: "CAD ($)",
  aud: "AUD ($)",
  jpy: "JPY (¥)",
};

export const BillingPage: React.FC = () => {
  const { getIdToken, user } = useAuth();
  const { isAdminOrOwner } = usePermissions();
  const { isEmbedded, embedSource } = useEmbedStore();
  const [subscription, setSubscription] = useState<SubscriptionDto | null>(
    null
  );
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isAnnual, setIsAnnual] = useState(false);
  const [checkoutLoading, setCheckoutLoading] = useState<string | null>(null);
  const [portalLoading, setPortalLoading] = useState(false);
  const [creditDetails, setCreditDetails] = useState<CreditDetailsResponse | null>(null);
  const [creditsLoading, setCreditsLoading] = useState(true);
  const [pricing, setPricing] = useState<PricingResponse | null>(null);
  const [selectedCurrency, setSelectedCurrency] = useState<string>("usd");
  const [topUpModalOpen, setTopUpModalOpen] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    loadSubscription();
    loadCreditDetails();
    loadPricing();
  }, []);

  // Detect Stripe checkout return → fire first_subscription GTM event and clean URL
  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    if (!sessionId) return;

    // localStorage dedup — prevents double-fire on hard refresh before URL is cleaned
    const uid = user?.id;
    const dedupKey = uid ? `sub:${uid}:first_subscription_fired` : null;
    if (dedupKey && localStorage.getItem(dedupKey)) {
      searchParams.delete('session_id');
      setSearchParams(searchParams, { replace: true });
      return;
    }

    if (dedupKey) localStorage.setItem(dedupKey, 'true');
    analytics.track('first_subscription');
    searchParams.delete('session_id');
    setSearchParams(searchParams, { replace: true });
  }, [searchParams, setSearchParams, user?.id]);

  // Refresh credits when page becomes visible (user navigates back)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        loadCreditDetails();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  // Auto-toggle to annual if user's current subscription is annual
  useEffect(() => {
    if (subscription?.interval === "year") {
      setIsAnnual(true);
    }
  }, [subscription?.interval]);

  const loadSubscription = async () => {
    try {
      setLoading(true);
      setError(null);
      const token = await getIdToken();
      if (!token) {
        setError("Authentication required");
        return;
      }
      const data = await billingApi.getSubscription(token);
      setSubscription(data);
    } catch (err: any) {
      console.error("Error loading subscription:", err);
      setError(err.response?.data?.message || "Failed to load subscription");
    } finally {
      setLoading(false);
    }
  };

  const loadCreditDetails = async () => {
    try {
      setCreditsLoading(true);
      const token = await getIdToken();
      if (!token) {
        return;
      }
      const data = await creditsApi.getDetails(token);
      setCreditDetails(data);
    } catch (err: any) {
      console.error('Error loading credit details:', err);
      // Don't show error for credits - it's not critical
    } finally {
      setCreditsLoading(false);
    }
  };

  const loadPricing = async () => {
    try {
      const data = await billingApi.getPricing();
      setPricing(data);
      setSelectedCurrency(data.defaultCurrency);
    } catch (err: any) {
      console.error('Error loading pricing:', err);
      // Fallback to USD
    }
  };

  const handleUpgrade = async (planType: PlanType) => {
    if (planType === PlanType.ENTERPRISE) {
      window.open("https://calendly.com/optidev/demo/", "_blank", "noopener,noreferrer");
      return;
    }

    try {
      setCheckoutLoading(planType);
      const token = await getIdToken();
      if (!token) {
        toast.error("Authentication required");
        return;
      }

      const interval = isAnnual ? "year" : "month";
      const hasActiveSubscription = subscription?.stripeSubscriptionId;

      if (hasActiveSubscription) {
        // User has existing subscription - upgrade in-app with proration
        await billingApi.upgradeSubscription(
          token,
          planType as PlanType.STANDARD | PlanType.PRO,
          interval
        );

        const planName = planType.charAt(0).toUpperCase() + planType.slice(1);
        toast.success(`Successfully upgraded to ${planName} Plan`);

        // Refresh subscription data and credit details
        await loadSubscription();
        await loadCreditDetails();
        setCheckoutLoading(null);
      } else {
        // New subscription - use Stripe Checkout
        const { url } = await billingApi.createCheckoutSession(
          token,
          planType as PlanType.STANDARD | PlanType.PRO,
          interval,
          selectedCurrency
        );
        window.location.href = url;
      }
    } catch (err: any) {
      console.error("Error upgrading subscription:", err);
      toast.error(
        err.response?.data?.message || "Failed to upgrade subscription"
      );
      setCheckoutLoading(null);
    }
  };

  const handleManageBilling = async () => {
    try {
      setPortalLoading(true);
      const token = await getIdToken();
      if (!token) {
        toast.error("Authentication required");
        return;
      }

      const { url } = await billingApi.createPortalSession(token);

      // Redirect to Stripe Customer Portal
      window.location.href = url;
    } catch (err: any) {
      console.error("Error creating portal session:", err);
      toast.error(
        err.response?.data?.message || "Failed to open billing portal"
      );
      setPortalLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (error) {
    return (
      <Alert variant="destructive">
        <AlertCircle className="h-4 w-4" />
        <AlertDescription>{error}</AlertDescription>
      </Alert>
    );
  }

  const currentPlanType = subscription?.planType || PlanType.FREE;

  // Embed mode: show simplified billing view
  if (isEmbedded && embedSource === 'optisigns') {
    return (
      <>
        <EmbedBillingView
          subscription={subscription}
          creditDetails={creditDetails}
          creditsLoading={creditsLoading}
          onTopUp={() => setTopUpModalOpen(true)}
        />
        <TopUpCreditsModal
          isOpen={topUpModalOpen}
          onClose={() => {
            setTopUpModalOpen(false);
            loadCreditDetails();
          }}
        />
      </>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between pb-5 border-b border-border/40">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-lg bg-muted flex items-center justify-center">
            <CreditCard className="h-5 w-5 text-muted-foreground" />
          </div>
          <div>
            <h2 className="text-lg font-semibold">Plans & Billing</h2>
            <p className="text-sm text-muted-foreground">
              {currentPlanType === PlanType.FREE
                ? "Choose a plan to unlock more features"
                : `Currently on ${currentPlanType.charAt(0).toUpperCase() + currentPlanType.slice(1)} plan`}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {/* Billing toggle */}
          <div className="flex items-center gap-2 bg-muted/50 rounded-lg px-3 py-1.5">
            <Label
              htmlFor="annual-toggle"
              className={`text-xs cursor-pointer transition-all ${!isAnnual ? "font-semibold text-foreground" : "text-muted-foreground"}`}
            >
              Monthly
            </Label>
            <Switch
              id="annual-toggle"
              checked={isAnnual}
              onCheckedChange={setIsAnnual}
              className="data-[state=checked]:bg-primary scale-90"
            />
            <Label
              htmlFor="annual-toggle"
              className={`text-xs cursor-pointer transition-all ${isAnnual ? "font-semibold text-foreground" : "text-muted-foreground"}`}
            >
              Annual
            </Label>
          </div>

          {/* Currency */}
          {pricing && pricing.currencies.length > 1 && (
            <Select value={selectedCurrency} onValueChange={setSelectedCurrency}>
              <SelectTrigger className="w-[100px] h-8 text-xs">
                <SelectValue placeholder="Currency" />
              </SelectTrigger>
              <SelectContent>
                {pricing.currencies.map((code) => (
                  <SelectItem key={code} value={code}>
                    {CURRENCY_LABELS[code] || code.toUpperCase()}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </div>
      </div>

      {/* Pricing Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        {[PlanType.STANDARD, PlanType.PRO, PlanType.ENTERPRISE].map((planType) => {
          const info = PLAN_INFO[planType];
          const symbol = CURRENCY_SYMBOLS[selectedCurrency] || "$";

          let monthlyPrice: number | string = "Custom";
          let yearlyPrice: number | string = "Custom";

          if (planType === PlanType.STANDARD && pricing?.plans?.standard) {
            const monthlyAmount = pricing.plans.standard.monthly[selectedCurrency];
            const yearlyAmount = pricing.plans.standard.yearly[selectedCurrency];
            monthlyPrice = monthlyAmount ? monthlyAmount / 100 : 25;
            yearlyPrice = yearlyAmount ? yearlyAmount / 100 : 300;
          } else if (planType === PlanType.PRO && pricing?.plans?.pro) {
            const monthlyAmount = pricing.plans.pro.monthly[selectedCurrency];
            const yearlyAmount = pricing.plans.pro.yearly[selectedCurrency];
            monthlyPrice = monthlyAmount ? monthlyAmount / 100 : 50;
            yearlyPrice = yearlyAmount ? yearlyAmount / 100 : 600;
          }

          return (
            <PricingCard
              key={planType}
              name={info.name}
              description={info.description}
              monthlyPrice={monthlyPrice}
              yearlyPrice={yearlyPrice}
              features={info.features}
              featureHeader={info.featureHeader}
              monthlyCredits={info.monthlyCredits}
              isAnnual={isAnnual}
              ctaText={info.ctaText}
              onUpgrade={() => handleUpgrade(planType)}
              loading={checkoutLoading === planType}
              currentPlanType={currentPlanType}
              currentInterval={subscription?.interval || null}
              planType={planType}
              currencySymbol={symbol}
              disabled={!isAdminOrOwner}
            />
          );
        })}
      </div>

      {/* Current Plan & Credits */}
      {subscription && (
        <div>
          <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wider mb-3">
            Subscription Details
          </h3>
          <div className="space-y-3">
            <CurrentPlanCardWithCredits
              planType={currentPlanType}
              amount={subscription.amount}
              currency={subscription.currency}
              interval={subscription.interval}
              currentPeriodEnd={subscription.currentPeriodEnd}
              onManageBilling={handleManageBilling}
              onTopUp={() => setTopUpModalOpen(true)}
              loading={portalLoading}
              cancelAtPeriodEnd={subscription.cancelAtPeriodEnd}
              pendingPlan={subscription.pendingPlan}
              creditBalance={creditDetails?.balance ?? null}
              subscriptionBalance={creditDetails?.subscriptionBalance ?? null}
              topupBalance={creditDetails?.topupBalance ?? null}
              creditAllocated={creditDetails?.allocated ?? null}
              creditUsed={creditDetails?.currentCycleUsed ?? null}
              creditNextReset={creditDetails?.nextResetAt ?? null}
              creditsLoading={creditsLoading}
            />

            {/* Top Up Credits - paid plans only */}
            {currentPlanType !== PlanType.FREE && (
              <div className="flex flex-col gap-3 p-4 bg-muted/30 rounded-lg">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium">Need more credits?</p>
                    <p className="text-xs text-muted-foreground">Purchase additional credits for your workspace</p>
                  </div>
                  <Button onClick={() => setTopUpModalOpen(true)} disabled={!isAdminOrOwner} className="text-xs h-8 px-3 bg-foreground hover:bg-foreground/90 text-background">
                    Top up credits
                  </Button>
                </div>
                <div className="flex items-center gap-1.5 text-xs text-muted-foreground border-t border-border/40 pt-3">
                  <Sparkles className="h-3 w-3 text-purple-500" />
                  <span>Have a Claude subscription?</span>
                  <Link to="/dashboard/settings/workspace?claude=reconnect" className="text-purple-600 dark:text-purple-400 hover:underline font-medium">
                    Connect for unlimited usage
                  </Link>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Top Up Credits Modal */}
      <TopUpCreditsModal
        isOpen={topUpModalOpen}
        onClose={() => {
          setTopUpModalOpen(false);
          loadCreditDetails();
        }}
      />
    </div>
  );
};
